import React, { useMemo, useState } from 'react'

import { Box, Chip, Grid, MenuItem, Paper, Select, SelectChangeEvent, Skeleton, Typography } from '@mui/material'

import EmptyState from '../../../../../../shared/components/EmptyState/EmptyState.component'
import LockerBox from '../LockerBox/LockerBox.component'

import { joinStringsWithBullet } from '../../../../../../shared/functions/String/string.functions'
import { LockerBoxStatus } from '../../Lockers.types'
import styles from './LockerViewArea.styles'
import { LockerViewAreaProps } from './LockerViewArea.types'

type StatusFilterValue = LockerBoxStatus | 'all'

const STATUS_LABELS: Record<LockerBoxStatus, string> = {
  available: 'Available',
  reserved: 'Reserved',
  occupied: 'Occupied',
  overdue: 'Overdue'
}

const STATUS_FILTER_OPTIONS: Array<{ label: string; value: StatusFilterValue }> = [
  { label: 'All Boxes', value: 'all' },
  { label: STATUS_LABELS.available, value: 'available' },
  { label: STATUS_LABELS.reserved, value: 'reserved' },
  { label: STATUS_LABELS.occupied, value: 'occupied' },
  { label: STATUS_LABELS.overdue, value: 'overdue' }
]

const lockerStatusSet = new Set<LockerBoxStatus>(Object.keys(STATUS_LABELS) as LockerBoxStatus[])

const normalizeStatus = (status?: string): LockerBoxStatus | null => {
  if (!status) {
    return null
  }

  const normalized = status.toLowerCase() as LockerBoxStatus

  return lockerStatusSet.has(normalized) ? normalized : null
}

const LockerViewArea: React.FC<LockerViewAreaProps> = (props) => {
  const { boxes, loading, onBoxClick, loadingBoxId } = props
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all')

  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilterValue, number> = {
      all: 0,
      available: 0,
      reserved: 0,
      occupied: 0,
      overdue: 0
    }

    if (!boxes) {
      return counts
    }

    boxes.forEach((bank) => {
      bank.boxes.forEach((box) => {
        counts.all += 1
        const statusKey = normalizeStatus(box.status)

        if (statusKey) {
          counts[statusKey] += 1
        }
      })
    })

    return counts
  }, [boxes])

  const handleFilterChange = (event: SelectChangeEvent<StatusFilterValue>) => {
    setStatusFilter(event.target.value as StatusFilterValue)
  }

  const filteredBoxes = useMemo(() => {
    if (!boxes) return []

    return boxes
      .map((bank) => ({
        ...bank,
        boxes: bank.boxes.filter((box) => {
          if (statusFilter === 'all') {
            return true
          }

          return normalizeStatus(box.status) === statusFilter
        })
      }))
      .filter((bank) => bank.boxes.length > 0)
  }, [boxes, statusFilter])

  if (loading) {
    return (
      <Box sx={styles.wrapper}>
        <Box sx={styles.headerRow}>
          <Skeleton variant="text" width={180} height={28} />
          <Skeleton variant="rounded" width={180} height={40} />
        </Box>

        <Grid container spacing={2} sx={styles.loadingGrid}>
          {[...Array(8)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Skeleton variant="rounded" height={160} />
            </Grid>
          ))}
        </Grid>
      </Box>
    )
  }

  if (!boxes || boxes.length === 0) {
    return (
      <EmptyState
        title="No locker data available yet"
        subtitle="Locker banks will appear here once the SmartLocker integration syncs successfully."
      />
    )
  }

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.headerRow}>
        <Typography variant="h6" fontWeight={700}>
          Lockers · {boxes?.length ?? 0}
        </Typography>

        <Box sx={styles.filterControl}>
          <Select
            value={statusFilter}
            size="small"
            onChange={handleFilterChange}
            MenuProps={{ disableScrollLock: true }}
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {joinStringsWithBullet([option.label, `${statusCounts[option.value]}`])}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {filteredBoxes.length === 0 ? (
        <EmptyState
          title="No lockers match this filter"
          subtitle="Try selecting a different status to continue monitoring locker health."
          marginTop={2}
        />
      ) : (
        filteredBoxes.map((bank) => (
          <Paper key={bank.id} sx={styles.bankSection} elevation={0}>
            <Box sx={styles.bankSectionHeader}>
              <Typography variant="subtitle1" fontWeight={600}>
                {joinStringsWithBullet([`${bank.name} (${bank.cityIataCode})`, `${bank.boxes.length} boxes`])}
              </Typography>

              <Chip label={bank.externalBankId} size="medium" sx={styles.externalBankIdChip} />
            </Box>

            <Grid container spacing={2}>
              {bank.boxes.map((box) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={box.id}>
                  <LockerBox
                    boxId={box.doorId || box.id}
                    status={normalizeStatus(box.status) ?? 'available'}
                    statusLabel={STATUS_LABELS[normalizeStatus(box.status) ?? 'available'] ?? box.status}
                    itemRequestId={box.itemRequestId}
                    cabinetId={box.cabinetId}
                    sizeId={box.sizeId}
                    doorId={box.doorId}
                    lockerNumber={box.lockerNumber}
                    lockerBankName={bank.name}
                    cityIataCode={bank.cityIataCode}
                    traveler={box.traveler}
                    shopper={box.shopper}
                    isLoading={loadingBoxId === (box.doorId || box.id)}
                    onClick={() => onBoxClick(box.doorId || box.id)}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>
        ))
      )}
    </Box>
  )
}

export default LockerViewArea
